import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Train, Map, Compass, ShoppingBag, MessageCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { whatsappUrl } from "../lib/whatsapp";

/**
 * TravellerDock — sticky bottom action bar on MOBILE ONLY.
 *
 * Designed for in-trip use from a phone:
 *  - one-tap to live timetable search (Plan)
 *  - one-tap to interactive Atlas
 *  - one-tap to Tours
 *  - one-tap to Cart
 *  - one-tap to SOS / help (mailto / tel link — placeholder for future
 *    conductor-on-call feature)
 *
 * Hidden on desktop (>= md) where the top nav is plenty.
 * Hidden on auth/checkout pages where it would crowd the form.
 */
const HIDDEN_PATHS = ["/login", "/register", "/checkout", "/checkout/success"];

const ITEMS = [
  { to: "/plan-trip", label: "Plan", icon: Train, testid: "dock-plan" },
  { to: "/map", label: "Atlas", icon: Map, testid: "dock-atlas" },
  { to: "/routes", label: "Tours", icon: Compass, testid: "dock-tours" },
  { to: "/cart", label: "Cart", icon: ShoppingBag, testid: "dock-cart", showBadge: true },
];

export default function TravellerDock() {
  const { pathname } = useLocation();
  const { count } = useCart();

  if (HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }

  return (
    <>
      {/* spacer so page content isn't hidden behind the fixed bar */}
      <div className="md:hidden h-[72px]" aria-hidden="true" />

      <nav
        data-testid="traveller-dock"
        aria-label="Quick actions"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-ink border-t border-panel-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-5">
          {ITEMS.map(({ to, label, icon: Icon, testid, showBadge }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px] transition-colors ${
                  isActive ? "text-gold" : "text-cream-dim hover:text-cream"
                }`
              }
            >
              <Icon className="h-5 w-5" strokeWidth={1.8} />
              <span className="font-display text-[10px] tracking-[0.15em] uppercase">
                {label}
              </span>
              {showBadge && count > 0 && (
                <span className="absolute top-1.5 right-[calc(50%-22px)] bg-gold text-ink text-[9px] font-display tracking-wider rounded-full h-4 min-w-4 px-1 inline-flex items-center justify-center">
                  {count}
                </span>
              )}
            </NavLink>
          ))}

          {/* WhatsApp conductor — opens a pre-filled chat with the booking ref */}
          <a
            href={whatsappUrl()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="dock-help"
            className="relative flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px] text-cream-dim hover:text-gold transition-colors"
          >
            <span className="relative">
              <MessageCircle className="h-5 w-5" strokeWidth={1.8} />
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-gold animate-pulse" />
            </span>
            <span className="font-display text-[10px] tracking-[0.15em] uppercase">
              Help
            </span>
          </a>
        </div>
      </nav>
    </>
  );
}
